import * as React from 'react';
import { useMemo, useState } from 'react';

import {
	createXpcAwcNavigationReporter,
	type XpcAwcClient,
} from '@atlaskit/analytics-cross-product/xpcAwcNavigationReporter';
import { createXpcDefaultDestinationRegistry } from '@atlaskit/analytics-cross-product/xpcDefaultDestinationRegistry';
import { XpcDestinationResolver } from '@atlaskit/analytics-cross-product/xpcDestinationResolver';
import { XpcNavigationProvider } from '@atlaskit/analytics-cross-product/xpcNavigationProvider';
import { XpcNavigationObservation } from '@atlaskit/analytics-cross-product/xpcNavigationObservation';
import type { AnalyticsWebClient } from '@atlaskit/analytics-listeners/types';
import { getDocument } from '@atlaskit/browser-apis';
import FeatureGates from '@atlaskit/feature-gate-js-client/feature-gates';

export interface HelpCenterXpcNavigationObserverProps {
	analyticsClient: XpcAwcClient;
	children?: React.ReactNode;
	initialArrivalUrl?: string;
	root?: Document | null;
}

export const adaptAnalyticsWebClientForXpc = (
	analyticsClient: Pick<AnalyticsWebClient, 'sendTrackEvent'>,
): XpcAwcClient => ({
	sendTrackEvent: (event) => {
		analyticsClient.sendTrackEvent(event);
		return Promise.resolve();
	},
});

const XPC_STREAM3_OBSERVATION_GATE = 'studio_xpc_stream3_observation';
const HELP_CENTER_XPC_REGISTRY = createXpcDefaultDestinationRegistry();
const HELP_CENTER_XPC_SOURCE = {
	product: XpcDestinationResolver.createProductId('jira'),
	subProduct: XpcDestinationResolver.createSubProductId('help-center'),
};
const HELP_CENTER_XPC_SURFACES = {
	native: XpcNavigationObservation.createSurface('help-center-root'),
};
const isHelpCenterXpcStream3ObservationEnabled = () =>
	FeatureGates.checkGate(XPC_STREAM3_OBSERVATION_GATE);

const getHelpCenterInitialArrivalUrl = (root: Document | null): string | undefined => {
	const initialNavigationUrl =
		root?.defaultView?.performance?.getEntriesByType?.('navigation')[0]?.name;

	return initialNavigationUrl ?? root?.location.href;
};

export const HelpCenterXpcNavigationObserver = ({
	analyticsClient,
	children,
	initialArrivalUrl,
	root = getDocument(),
}: HelpCenterXpcNavigationObserverProps): React.JSX.Element => {
	const [resolvedInitialArrivalUrl] = useState(
		() => initialArrivalUrl ?? getHelpCenterInitialArrivalUrl(root),
	);
	const reporter = useMemo(
		() => createXpcAwcNavigationReporter(analyticsClient),
		[analyticsClient],
	);

	return (
		<XpcNavigationProvider
			baseUrl={root?.baseURI ?? 'https://support.atlassian.com'}
			initialArrivalUrl={resolvedInitialArrivalUrl}
			isEnabled={isHelpCenterXpcStream3ObservationEnabled}
			registry={HELP_CENTER_XPC_REGISTRY}
			reporter={reporter}
			root={root}
			source={HELP_CENTER_XPC_SOURCE}
			surfaces={HELP_CENTER_XPC_SURFACES}
		>
			{children}
		</XpcNavigationProvider>
	);
};
